import "./_monCompte.scss";

import cx from "classnames";
import {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";

import {fetchAdresse} from "@/api/adresse";
import {fetchCompte} from "@/api/compte";
import {fetchHistorique} from "@/api/historique";
import {FetchOperationsCommerciales} from "@/api/operationCommerciale";
import BlocLabel from "@/components/BlocLabel/BlocLabel";
import Button from "@/components/Button/Button";
import {Error} from "@/components/Error/Error";
import HistoriqueBloc from "@/components/Historique/HistoriqueBloc";
import HistoriqueMassificationContent from "@/components/Historique/HistoriqueMassificationContent";
import LineInfo from "@/components/LineInfo/LineInfo";
import Loader from "@/components/Loader/loader";
import {Pagination} from "@/components/Paggination/Paggination";
import Section from "@/components/Section/Section";
import Title from "@/components/Section/Title";
import {useAwaitApiReponse} from "@/hooks/useAwaitApiReponse";
import {Adresse, ApiReponseHistorique, CompteInfo} from "@/ts/interface/compte";
import {ApiReponseCardOperationCommerciale} from "@/ts/interface/operationCommerciale";

function MonCompte(): JSX.Element {
  const navigate = useNavigate();
  const [adresse, setAdresse] = useState<{adresses: Adresse[]} | null>(null);
  const [historiquePrint, setHistoriquePrint] = useState<
    "Massifications" | "Opérations commerciales"
  >("Massifications");
  const [currentCompteInfo, setCurrentCompteInfo] = useState<CompteInfo | null>(
    null,
  );
  const [historiqueMassification, setHistoriqueMassification] =
    useState<ApiReponseHistorique | null>(null);
  const [historiqueOperation, setHistoriqueOperation] =
    useState<ApiReponseCardOperationCommerciale | null>(null);
  const [inLoad, setInLoad] = useState<boolean>(true);
  const [paginationMassificationSearch, setPaginationMassificationSearch] =
    useState<{
      page: number;
      itemsPerPage: number;
    }>({page: 1, itemsPerPage: 10});
  const [paginationOperationSearch, setPaginationOperationSearch] = useState<{
    page: number;
    itemsPerPage: number;
  }>({page: 1, itemsPerPage: 10});

  const {
    getReponse: getHistoriqueOperationCommerciale,
    loading: loadingHistoriqueOperationCommerciale,
    error: errorHistoriqueOperationCommerciale,
  } = useAwaitApiReponse();

  // On appel tout les endpoints au premier chargement
  // Ensuite on cible sur massification ou opération commerciale
  // En fonction de l'historique que l'on affiche et des changements de pagination.
  useEffect(() => {
    const getCompteInfo = async (): Promise<void> => {
      setInLoad(true);
      try {
        if (!currentCompteInfo) {
          try {
            const reponseCompte = await fetchCompte();

            setCurrentCompteInfo(reponseCompte || null);
          } catch (error) {
            console.error(
              "Erreur lors de la récupération des informations :",
              error,
            );
          }
        }
        if (!adresse) {
          try {
            const reponseAdresse = await fetchAdresse();

            setAdresse(reponseAdresse || null);
          } catch (error) {
            console.error(
              "Erreur lors de la récupération des informations :",
              error,
            );
          }
        }
        if (historiquePrint === "Massifications") {
          try {
            const reponseHistorique = await fetchHistorique(
              paginationMassificationSearch.page,
              paginationMassificationSearch.itemsPerPage,
            );
            if (reponseHistorique?.historique) {
              setHistoriqueMassification(reponseHistorique);
            }
          } catch (error) {
            console.error(
              "Erreur lors de la récupération des informations :",
              error,
            );
          }
        }

        if (historiquePrint === "Opérations commerciales") {
          try {
            const reponseOperations = await getHistoriqueOperationCommerciale(
              () =>
                FetchOperationsCommerciales(
                  ["ordered=true"],
                  paginationOperationSearch?.page,
                  paginationOperationSearch.itemsPerPage,
                ),
            );
            if (reponseOperations) {
              setHistoriqueOperation(reponseOperations);
            }
          } catch (error) {
            console.error(
              "Erreur lors de la récupération des informations :",
              error,
            );
          }
        }
      } catch (error) {
        console.error(
          "Erreur lors de la récupération des informations :",
          error,
        );
      } finally {
        setInLoad(false); //S'assure que setInLoad soit appelé dans tous les cas
      }
    };
    getCompteInfo();
  }, [
    paginationMassificationSearch,
    paginationOperationSearch,
    historiquePrint,
  ]);
  let InformationComponent: JSX.Element | null = null;
  let AdresseComponent: JSX.Element[] | null | undefined = null;
  let HistoriqueMassificationComponents: JSX.Element[] | null | undefined =
    null;
  let HistoriqueOperationComponents: JSX.Element[] | null | undefined = null;

  if (currentCompteInfo) {
    const {nom, prenom, email} = currentCompteInfo;

    InformationComponent = (
      <>
        <LineInfo
          label="Nom"
          value={nom}
          size="large"
          onChange={e => {
            setCurrentCompteInfo({
              ...currentCompteInfo,
              nom: e.target.value ? e.target.value : "",
            });
          }}
        />
        <LineInfo
          label="Prenom"
          value={prenom}
          size="large"
          onChange={e => {
            setCurrentCompteInfo({
              ...currentCompteInfo,
              prenom: e.target.value ? e.target.value : "",
            });
          }}
        />
        <LineInfo
          label="Email"
          value={email}
          size="large"
          onChange={e => {
            setCurrentCompteInfo({
              ...currentCompteInfo,
              email: e.target.value ? e.target.value : "",
            });
          }}
        />
      </>
    );

    AdresseComponent = adresse?.adresses.map((lieu, index) => {
      const printIsDefault: string =
        lieu.is_default === true ? " (Par Default)" : " ";
      return (
        <div className={cx("adresse-selection")} key={index}>
          <LineInfo
            label={lieu.libelle ? lieu.libelle + printIsDefault : "Adresse"}
            value={lieu.value}
            type="adresse"
          />
        </div>
      );
    });

    // historique massifications
    HistoriqueMassificationComponents = historiqueMassification?.historique.map(
      (engagement, index) => {
        return (
          <HistoriqueBloc
            id={engagement.engagement_id}
            label={engagement.offre.nom}
            type={engagement.type}
            key={index}>
            <HistoriqueMassificationContent
              articles={engagement.articles}
              status={null}
              adresse={engagement.adresse}
            />
          </HistoriqueBloc>
        );
      },
    );
  }
  const HistoriqueMassificationBloc = historiqueMassification ? (
    <>
      {HistoriqueMassificationComponents}
      <Pagination
        pagination={historiqueMassification?.pagination}
        onChangePage={value => {
          // relance le loader
          setHistoriqueMassification(null);

          // declanche la recherche
          setPaginationMassificationSearch({
            ...paginationMassificationSearch,
            page: value,
          });
        }}
        onChangeItemsPerPage={value => {
          // relance le loader
          setHistoriqueMassification(null);

          // declanche la recherche
          setPaginationMassificationSearch({
            ...paginationMassificationSearch,
            itemsPerPage: value,
          });
        }}
      />
    </>
  ) : inLoad ? (
    <Loader />
  ) : (
    <Error text="Erreur lors du chargement des données" />
  );

  // historique opérations commerciales
  HistoriqueOperationComponents = historiqueOperation?.operations?.map(
    (operation, index) => {
      return (
        <HistoriqueBloc
          label={operation.nom}
          type={"operationCommerciale"}
          key={index}>
          <div className="historique-operation">
            <img
              src={operation.images?.[0]?.url}
              alt={`image présentation de l'opération ${operation.nom}`}
            />
            <div className="historique-operation--content">
              <h3>{operation.nom}</h3>
              <p dangerouslySetInnerHTML={{__html: operation.description}}></p>
            </div>
            <Button
              label="Mes commandes"
              variant="secondary"
              onClick={() => {
                navigate(
                  `/co-deals/mon_compte/operation_commerciale/${operation.operation_id}`,
                );
              }}
            />
          </div>
        </HistoriqueBloc>
      );
    },
  );

  const HistoriqueOperationBloc = historiqueOperation ? (
    <>
      {HistoriqueOperationComponents}
      <Pagination
        pagination={historiqueOperation?.pagination}
        onChangePage={value => {
          // relance le loader
          setHistoriqueOperation(null);

          // declanche la recherche
          setPaginationOperationSearch({
            ...paginationOperationSearch,
            page: value,
          });
        }}
        onChangeItemsPerPage={value => {
          // relance le loader
          setHistoriqueOperation(null);

          // declanche la recherche
          setPaginationOperationSearch({
            ...paginationOperationSearch,
            itemsPerPage: value,
          });
        }}
      />
    </>
  ) : loadingHistoriqueOperationCommerciale &&
    !errorHistoriqueOperationCommerciale ? (
    <Loader />
  ) : (
    <Error text={errorHistoriqueOperationCommerciale.message} />
  );
  return (
    <Section id="compte">
      <Title title="Mon compte" />
      <div className="compte">
        <div id="compte-info">
          <BlocLabel label="Mes informations" />
          {currentCompteInfo ? (
            InformationComponent
          ) : inLoad ? (
            <Loader />
          ) : (
            <Error text="Erreur lors du chargement des données" />
          )}
        </div>
        <div id="compte-adresse">
          <BlocLabel label="Mes adresses" />
          {adresse ? (
            AdresseComponent
          ) : inLoad ? (
            <Loader />
          ) : (
            <Error text="Erreur lors du chargement des données" />
          )}
        </div>
      </div>
      <Title title="Historique" />
      <div className="compte-historique">
        <div className="compte-historique--onglet">
          <button
            className={cx(
              "compte-historique",
              historiquePrint === "Massifications" && "active",
            )}
            onClick={() => {
              setHistoriquePrint("Massifications");
              setInLoad(true);
            }}>
            Massifications
          </button>
          <button
            className={cx(
              "compte-historique",
              historiquePrint === "Opérations commerciales" && "active",
            )}
            onClick={() => {
              setHistoriquePrint("Opérations commerciales");
              setInLoad(true);
            }}>
            Operations commerciales
          </button>
        </div>
        {historiquePrint === "Massifications" && HistoriqueMassificationBloc}
        {historiquePrint === "Opérations commerciales" &&
          HistoriqueOperationBloc}
      </div>
    </Section>
  );
}

export default MonCompte;
